import React, { memo, useCallback, useState } from 'react'

import { Notice, Icon, AIGeneratedGradient } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

const SEEN_KEY = 'remixModeNoticeSeen'

const hasBeenSeen = () => {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1'
  } catch (_) {
    return false
  }
}

const markSeen = () => {
  try {
    window.localStorage.setItem(SEEN_KEY, '1')
  } catch (_) {
    // Storage may be unavailable (private mode, quota). Treat as no-op.
  }
}

// Remix-mode indicator shown whenever the user opens a community model from the
// gallery. Origin rides on the navigation itself: Gallery navigates with
// `state: { fromGallery: true }`, so any other way of opening a model (library card,
// deep link, refresh) has no such state and nothing renders - no flag to reset
// anywhere. It is not tied to a specific model: opening another gallery model is a
// fresh navigation that re-flags fromGallery.
//
// Two variants share this one origin signal:
//   - First encounter: the full "You are in Remix Mode" notice, dismissible.
//     The seen flag is persisted only when the user closes the notice - not when
//     it merely displays - so a transient render (e.g. a route remount) never
//     burns the one-time flag.
//   - Every later encounter (seen flag already set at mount): a compact "Remix
//     Mode" tag instead of the verbose notice.
//
// `seenOnMount` is frozen at mount, so closing the notice within the current
// session only hides it - the tag appears on the next gallery-open (a fresh
// mount), not the instant the notice is dismissed.
function RemixModeNotice() {
  const t = useTranslation()
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}
  const location = useLocation()
  const fromGallery = location?.state?.fromGallery === true
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const [seenOnMount] = useState(hasBeenSeen)
  const [dismissed, setDismissed] = useState(false)

  const handleClose = useCallback(() => {
    markSeen()
    setDismissed(true)
  }, [])

  if (!isLoggedIn || !fromGallery || openedModelId == null) {
    return null
  }

  if (seenOnMount) {
    return <div className="remix-mode-tag cgt-body-base-semibold">{t('viewer.remixNotice.tag')}</div>
  }

  if (dismissed) {
    return null
  }

  return (
    <div className="remix-mode-notice" role="status" aria-live="polite">
      <Notice
        useSSRIcon
        ssrIcon={<Icon svgComponent={AIGeneratedGradient} notSpaced size={iconSizeMedium} />}
        onClose={handleClose}
      >
        <div className="remix-mode-notice__body">
          <span className="cgt-heading-50">{t('viewer.remixNotice.title')}</span>
          <span className="cgt-body-base">
            {t('viewer.remixNotice.description')}
          </span>
        </div>
      </Notice>
    </div>
  )
}

export default memo(RemixModeNotice)
